import { useState, useEffect, useRef } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { TimelinePreview } from "../components/TimelinePreview";
import { toast } from "sonner";
import {
  CheckCircle2, AlertCircle, Loader2, Play, Copy, ExternalLink,
  Download, Sparkles, XCircle, Clock, Film, RefreshCw, Zap
} from "lucide-react";

export default function RenderStep({ session, onUpdate, allClipsSelected, productionComplete, shots, production }) {
  const [rendering, setRendering] = useState(false);
  const [quality, setQuality] = useState("standard");
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const renderStartRef = useRef(null);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!session?.id) return;
    setLoadingPreview(true);
    api.get(`/sessions/${session.id}/render-preview`)
      .then(res => setPreviewData(res.data))
      .catch(() => {})
      .finally(() => setLoadingPreview(false));
  }, [session?.id]);

  // Track elapsed time while rendering
  useEffect(() => {
    if (session.status !== "Rendering") {
      renderStartRef.current = null;
      setElapsed(0);
      return;
    }
    const startedAt = session.render_status?.started_at;
    renderStartRef.current = startedAt ? new Date(startedAt) : new Date();
    const timer = setInterval(() => {
      setElapsed(Math.floor((Date.now() - renderStartRef.current.getTime()) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [session.status, session.render_status?.started_at]);

  // Poll while rendering
  useEffect(() => {
    if (session.status !== "Rendering") return;
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/sessions/${session.id}/render-status`);
        if (res.data.status !== "Rendering") {
          const full = await api.get(`/sessions/${session.id}`);
          onUpdate(full.data);
          clearInterval(interval);
          if (res.data.status === "Delivered") toast.success("Your video is ready!");
          if (res.data.status === "RenderFailed") toast.error("Something went wrong creating your video.");
        } else {
          const full = await api.get(`/sessions/${session.id}`);
          onUpdate(full.data);
        }
      } catch {}
    }, 4000);
    return () => clearInterval(interval);
  }, [session.status, session.id, onUpdate]);

  const handleRender = async () => {
    setRendering(true);
    try {
      await api.post(`/sessions/${session.id}/render`, { mode: quality });
      const full = await api.get(`/sessions/${session.id}`);
      onUpdate(full.data);
      toast.success("Your video is now being created!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    }
    setRendering(false);
  };

  const handleReRender = async () => {
    setRendering(true);
    try {
      await api.post(`/sessions/${session.id}/re-render`, { mode: quality });
      const full = await api.get(`/sessions/${session.id}`);
      onUpdate(full.data);
      toast.success("Re-creating your video...");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    }
    setRendering(false);
  };

  const formatElapsed = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };

  // Checklist
  const selectedCount = shots.filter(s => s.selected_candidate_id).length;
  const checks = [
    { label: "All clips selected", done: allClipsSelected, detail: `${selectedCount}/${shots.length} shots` },
    { label: "Agent video ready", done: production.agent_composite?.status === 'ready' },
    { label: "Intro video ready", done: production.avatar_intro?.video_status === 'ready' },
    { label: "Voiceover ready", done: production.audio_walkthrough?.status === 'ready' },
    { label: "Closing video ready", done: production.avatar_cta?.video_status === 'ready' },
    { label: "Highlight shots chosen", done: (production.wow_shots?.length || 0) >= 3 },
  ];
  const readyCount = checks.filter(c => c.done).length;
  const allReady = readyCount === checks.length;

  // ===== RENDERING STATE =====
  if (session.status === "Rendering") {
    const rs = session.render_status || {};
    const rawStep = rs.progress || 'Rendering...';

    const stepLabels = {
      'Starting render worker...': 'Getting things ready...',
      'Aligning voiceover to clips...': 'Syncing voiceover with video...',
      'Preparing render...': 'Preparing your video...',
      'Probing clip durations...': 'Analyzing your clips...',
      'Building video timeline...': 'Assembling your video...',
      'Uploading video...': 'Almost done — saving your video...',
      'Rendering...': 'Creating your video...',
    };

    const progressMap = {
      'Starting render worker...': 3,
      'Aligning voiceover to clips...': 5,
      'Preparing render...': 8,
      'Probing clip durations...': 45,
      'Building video timeline...': 50,
      'Uploading video...': 90,
    };

    let step = stepLabels[rawStep] || 'Creating your video...';
    let pct = progressMap[rawStep] || 50;
    if (rawStep.startsWith('Downloading assets...')) {
      const m = rawStep.match(/\((\d+)\/(\d+)\)/);
      if (m) {
        const [, done, total] = m;
        pct = 10 + Math.round((parseInt(done) / parseInt(total)) * 35);
        step = `Downloading files (${done} of ${total})...`;
      } else {
        pct = 12;
        step = 'Downloading your files...';
      }
    } else if (rawStep.startsWith('Encoding video')) {
      pct = 55;
      step = 'Putting it all together...';
    }

    return (
      <div className="p-8 max-w-lg mx-auto" data-testid="render-status-view">
        <div className="bg-white rounded-xl border border-[#E2E4E7] p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-2xl bg-[#4D11F6]/10 flex items-center justify-center mx-auto mb-4">
              <Film size={28} className="text-[#4D11F6] animate-pulse" />
            </div>
            <h2 className="text-xl font-bold text-[#020B23]" style={{ fontFamily: 'Sora, sans-serif' }}>
              Creating Your Video
            </h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-[#F3F3F9]">
              <div className="flex items-center gap-2">
                <Loader2 size={14} className="text-[#4D11F6] animate-spin" />
                <span className="text-sm font-medium text-[#1A2238]">{step}</span>
              </div>
              {elapsed > 0 && (
                <span className="text-xs font-mono text-[#94969C]" data-testid="render-elapsed">{formatElapsed(elapsed)}</span>
              )}
            </div>

            <Progress value={pct} className="h-2" />

            <p className="text-xs text-center text-[#94969C]">
              Your video is being created. This usually takes a few minutes.
            </p>

            {elapsed > 60 && (
              <div className="pt-2 text-center">
                <p className="text-[11px] text-amber-600 mb-2">
                  Taking longer than expected? You can retry if it seems stuck.
                </p>
                <Button variant="outline" size="sm" onClick={handleReRender} disabled={rendering}
                  className="border-amber-300 text-amber-700 hover:bg-amber-50 text-xs">
                  {rendering ? <Loader2 size={12} className="mr-1 animate-spin" /> : <RefreshCw size={12} className="mr-1" />}
                  Cancel &amp; Retry
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ===== RENDER FAILED STATE =====
  if (session.status === "RenderFailed") {
    const rs = session.render_status || {};
    return (
      <div className="p-8 max-w-lg mx-auto" data-testid="render-failed-view">
        <div className="bg-white rounded-xl border border-red-200 p-8 text-center">
          <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
            <XCircle size={28} className="text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-[#020B23] mb-1" style={{ fontFamily: 'Sora, sans-serif' }}>Video Creation Failed</h2>
          <div className="text-left p-3 rounded-lg bg-red-50 border border-red-100 mb-4 mt-3">
            <p className="text-xs text-red-700 break-all">{rs.error || 'Something went wrong while creating your video. Please try again.'}</p>
          </div>
          <Button onClick={handleReRender} disabled={rendering}
            data-testid="retry-render-btn"
            className="bg-[#4D11F6] hover:bg-[#3A0BC0] text-white text-sm">
            {rendering ? <Loader2 size={14} className="mr-2 animate-spin" /> : <RefreshCw size={14} className="mr-2" />}
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // ===== DELIVERED STATE =====
  if (session.status === "Delivered") {
    const rs = session.render_status || {};
    const videoUrl = session.delivery_url || rs.video_url;
    const completedAt = rs.completed_at;
    const currentQualityLabel = rs.mode === 'high' ? 'High' : 'Standard';
    return (
      <div className="p-8 max-w-xl mx-auto" data-testid="delivery-view">
        <div className="bg-white rounded-xl border border-[#E2E4E7] overflow-hidden">
          {videoUrl && (
            <div className="bg-black">
              <video src={videoUrl} controls className="w-full" style={{ maxHeight: '400px' }}
                data-testid="delivered-video-player" />
            </div>
          )}

          <div className="p-6 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <CheckCircle2 size={20} className="text-emerald-500" />
              <h2 className="text-lg font-bold text-[#020B23]" style={{ fontFamily: 'Sora, sans-serif' }}>
                Video Ready
              </h2>
            </div>
            {completedAt && (
              <p className="text-xs text-[#94969C] mb-4">
                Rendered on {new Date(completedAt).toLocaleString()} · {currentQualityLabel} quality
              </p>
            )}
            {videoUrl && (
              <div className="flex gap-3 justify-center">
                <Button variant="outline"
                  onClick={() => { navigator.clipboard.writeText(videoUrl); toast.success("Link copied!"); }}
                  data-testid="copy-link-btn"
                  className="border-[#E2E4E7] text-sm">
                  <Copy size={14} className="mr-2" /> Copy Link
                </Button>
                <Button variant="outline"
                  onClick={() => window.open(videoUrl, '_blank')}
                  data-testid="open-video-btn"
                  className="border-[#E2E4E7] text-sm">
                  <ExternalLink size={14} className="mr-2" /> Open
                </Button>
                <Button
                  onClick={() => {
                    const a = document.createElement('a');
                    a.href = videoUrl;
                    a.download = `${session.property_label || 'video'}.mp4`;
                    a.click();
                  }}
                  data-testid="download-video-btn"
                  className="bg-[#4D11F6] hover:bg-[#3A0BC0] text-white text-sm">
                  <Download size={14} className="mr-2" /> Download
                </Button>
              </div>
            )}

            <div className="mt-6 pt-4 border-t border-[#E2E4E7] space-y-3 text-left">
              <div>
                <p className="text-xs font-semibold text-[#020B23] mb-2" style={{ fontFamily: 'Sora, sans-serif' }}>Re-render Quality</p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setQuality('standard')}
                    data-testid="re-render-quality-standard-btn"
                    className={`p-3 rounded-lg border-2 text-left transition-all ${quality === 'standard' ? 'border-[#4D11F6] bg-[#4D11F6]/5' : 'border-[#E2E4E7] hover:border-[#4D11F6]/30'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Zap size={14} className={quality === 'standard' ? 'text-[#4D11F6]' : 'text-[#94969C]'} />
                      <span className={`text-sm font-semibold ${quality === 'standard' ? 'text-[#4D11F6]' : 'text-[#1A2238]'}`}>Standard</span>
                    </div>
                    <p className="text-[11px] text-[#676970]">Faster render, good quality (CRF 24)</p>
                  </button>
                  <button
                    onClick={() => setQuality('high')}
                    data-testid="re-render-quality-high-btn"
                    className={`p-3 rounded-lg border-2 text-left transition-all ${quality === 'high' ? 'border-emerald-500 bg-emerald-50' : 'border-[#E2E4E7] hover:border-emerald-300'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles size={14} className={quality === 'high' ? 'text-emerald-600' : 'text-[#94969C]'} />
                      <span className={`text-sm font-semibold ${quality === 'high' ? 'text-emerald-700' : 'text-[#1A2238]'}`}>High</span>
                    </div>
                    <p className="text-[11px] text-[#676970]">Best quality render (CRF 20)</p>
                  </button>
                </div>
              </div>
              <Button variant="outline" onClick={handleReRender} disabled={rendering}
                data-testid="re-render-btn"
                className="border-[#E2E4E7] text-sm">
                {rendering ? <Loader2 size={14} className="mr-2 animate-spin" /> : <RefreshCw size={14} className="mr-2" />}
                Re-render
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== PRE-RENDER (CHECKLIST + TIMELINE + QUALITY) =====
  return (
    <div className="p-8 max-w-xl mx-auto space-y-5" data-testid="render-step-view">
      <div className="bg-white rounded-xl border border-[#E2E4E7] overflow-hidden">
        <div className="px-6 py-4 border-b border-[#E2E4E7]">
          <h2 className="text-lg font-bold text-[#020B23]" style={{ fontFamily: 'Sora, sans-serif' }}>Create Final Video</h2>
          <p className="text-sm text-[#676970] mt-0.5">Review the timeline, choose your quality, and create your walkthrough video.</p>
        </div>

        <div className="p-6 space-y-5">
          {/* Quality selector */}
          <div data-testid="quality-selector">
            <p className="text-xs font-semibold text-[#020B23] mb-2" style={{ fontFamily: 'Sora, sans-serif' }}>Quality</p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setQuality('standard')}
                data-testid="quality-standard-btn"
                className={`p-3 rounded-lg border-2 text-left transition-all ${quality === 'standard' ? 'border-[#4D11F6] bg-[#4D11F6]/5' : 'border-[#E2E4E7] hover:border-[#4D11F6]/30'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Zap size={14} className={quality === 'standard' ? 'text-[#4D11F6]' : 'text-[#94969C]'} />
                  <span className={`text-sm font-semibold ${quality === 'standard' ? 'text-[#4D11F6]' : 'text-[#1A2238]'}`}>Standard</span>
                </div>
                <p className="text-[11px] text-[#676970]">Faster processing, great quality</p>
              </button>
              <button
                onClick={() => setQuality('high')}
                data-testid="quality-high-btn"
                className={`p-3 rounded-lg border-2 text-left transition-all ${quality === 'high' ? 'border-emerald-500 bg-emerald-50' : 'border-[#E2E4E7] hover:border-emerald-300'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles size={14} className={quality === 'high' ? 'text-emerald-600' : 'text-[#94969C]'} />
                  <span className={`text-sm font-semibold ${quality === 'high' ? 'text-emerald-700' : 'text-[#1A2238]'}`}>High</span>
                </div>
                <p className="text-[11px] text-[#676970]">Best quality, takes a bit longer</p>
              </button>
            </div>
          </div>

          {/* Timeline Preview */}
          {previewData?.debug && !loadingPreview && (
            <div className="space-y-3">
              <TimelinePreview debug={previewData.debug} />
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-[#94969C]">
                <span>{previewData.debug.walkthrough_clips || 0} walkthrough clips</span>
                <span>{previewData.debug.wow_shots || 0} wow shots</span>
                {previewData.debug.has_intro && <span>Avatar intro</span>}
                {previewData.debug.has_cta && <span>Avatar CTA</span>}
                {previewData.debug.has_walkthrough_audio && <span>Voiceover</span>}
                <span className="text-emerald-600 font-medium">Ready to create</span>
              </div>
            </div>
          )}
          {loadingPreview && (
            <div className="flex items-center justify-center py-4">
              <Loader2 size={16} className="animate-spin text-[#94969C]" />
            </div>
          )}
        </div>
      </div>

      {/* Checklist */}
      <div className="bg-white rounded-xl border border-[#E2E4E7] overflow-hidden">
        <div className="px-6 py-3 border-b border-[#E2E4E7] flex items-center justify-between">
          <p className="text-xs font-semibold text-[#020B23]" style={{ fontFamily: 'Sora, sans-serif' }}>Ready to Go?</p>
          <span className={`text-xs font-medium ${allReady ? 'text-emerald-600' : 'text-[#94969C]'}`}>{readyCount}/{checks.length}</span>
        </div>
        <div className="p-4 space-y-1.5">
          {checks.map((c, i) => (
            <div key={i} className={`flex items-center justify-between py-2 px-3 rounded-lg ${c.done ? 'bg-emerald-50/40' : 'bg-[#F3F3F9]'}`}
              data-testid={`render-check-${i}`}>
              <div className="flex items-center gap-2.5">
                {c.done ? <CheckCircle2 size={14} className="text-emerald-500" /> : <AlertCircle size={14} className="text-[#D8D9DD]" />}
                <span className={`text-xs font-medium ${c.done ? 'text-[#020B23]' : 'text-[#94969C]'}`}>{c.label}</span>
              </div>
              {c.detail && <span className={`text-[10px] ${c.done ? 'text-emerald-600' : 'text-[#94969C]'}`}>{c.detail}</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Render Button */}
      <div className="flex items-center justify-between">
        {!allReady && <p className="text-xs text-amber-600">Please complete all items above before creating your video.</p>}
        <div className="ml-auto">
          <Button onClick={handleRender}
            disabled={!allReady || rendering}
            data-testid="start-render-btn"
            className={`text-sm px-6 ${allReady
              ? 'bg-[#4D11F6] hover:bg-[#3A0BC0] text-white'
              : 'bg-[#E2E4E7] text-[#94969C] cursor-not-allowed'}`}>
            {rendering ? (
              <><Loader2 size={14} className="mr-2 animate-spin" /> Starting...</>
            ) : (
              <><Play size={14} className="mr-2" /> Create Video</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
